/* Google model-viewer 4.3.1 is downloaded only after an explicit viewer request. */
(() => {
  'use strict';
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  class DMSceneViewer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      if (!this.querySelector('dialog')) {
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true });
        else if (!this.waitingForChildren) { this.waitingForChildren = true; requestAnimationFrame(() => { this.waitingForChildren = false; if (this.querySelector('dialog')) this.connectedCallback(); }); }
        return;
      }
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      this.stage = this.querySelector('[data-scene-stage]');
      this.canvas = this.querySelector('[data-scene-canvas]');
      this.loading = this.querySelector('[data-scene-loading]');
      this.status = this.querySelector('[data-scene-status]');
      this.retry = this.querySelector('[data-scene-retry]');
      this.controls = [...this.querySelectorAll('[data-scene-action]')];
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.observer = new MutationObserver(() => {
        if (this.dialog.open && !this.viewer && !this.pending) this.load();
      });
      this.observer.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
      this.dialog.addEventListener('close', () => this.release());
      this.retry.addEventListener('click', () => this.load());
      this.controls.forEach(button => button.addEventListener('click', () => this.move(button.dataset.sceneAction)));
    }
    disconnectedCallback() { this.observer?.disconnect(); this.release(); }
    async load() {
      if (this.pending) return;
      this.pending = true;
      const attempt = this.attempt = (this.attempt || 0) + 1;
      this.stage.dataset.loaded = 'false';
      this.stage.setAttribute('aria-busy', 'true');
      this.loading.hidden = false;
      this.retry.hidden = true;
      this.status.textContent = this.dataset.loading;
      this.controls.forEach(button => button.disabled = true);
      this.timer = setTimeout(() => this.fail(attempt), 30000);
      try {
        if (!customElements.get('model-viewer')) await import(this.dataset.module);
        if (!this.dialog.open || this.attempt !== attempt) return;
        const viewer = document.createElement('model-viewer');
        const a11y = this.querySelector('[data-scene-a11y]');
        if (a11y) viewer.a11y = JSON.parse(a11y.textContent);
        // model-viewer 4.3.1 detects HDR from the URL suffix. Preserve Shopify's
        // version query while providing the suffix in a network-neutral fragment.
        const environment = new URL(this.dataset.environment, document.baseURI);
        environment.hash = 'environment.hdr';
        const attributes = {
          alt: this.dataset.alt, 'camera-controls': '', 'disable-pan': '', 'disable-tap': '',
          'camera-target': '0m 0.565m 0m', 'camera-orbit': '27deg 73deg 1.02m',
          'min-camera-orbit': '-Infinity 28deg 0.68m', 'max-camera-orbit': 'Infinity 80deg 1.18m',
          'field-of-view': '40deg', 'min-field-of-view': '40deg', 'max-field-of-view': '40deg',
          'interaction-prompt': 'none', 'interpolation-decay': this.motion.matches ? '0' : '90',
          'environment-image': environment.href, 'shadow-intensity': '0', 'exposure': '1', 'touch-action': 'none', 'loading': 'eager',
          'reveal': 'auto', 'aria-describedby': `${this.dialog.id}-help`
        };
        Object.entries(attributes).forEach(([name, value]) => viewer.setAttribute(name, value));
        viewer.addEventListener('load', () => {
          if (this.attempt !== attempt) return;
          clearTimeout(this.timer); this.pending = false;
          this.stage.setAttribute('aria-busy', 'false');
          this.stage.dataset.loaded = 'true';
          this.status.textContent = this.dataset.ready;
          this.loading.hidden = true;
          this.controls.forEach(button => button.disabled = false);
        }, { once: true });
        viewer.addEventListener('error', () => this.fail(attempt), { once: true });
        this.viewer = viewer;
        this.canvas.replaceChildren(viewer);
        viewer.src = this.dataset.scene;
      } catch { this.fail(attempt); }
    }
    fail(attempt) {
      if (attempt !== this.attempt || !this.dialog.open) return;
      clearTimeout(this.timer); this.pending = false;
      this.viewer?.remove(); this.viewer = null;
      this.stage.setAttribute('aria-busy', 'false');
      this.status.textContent = this.dataset.error;
      this.retry.hidden = false;
    }
    move(action) {
      if (!this.viewer || this.stage.dataset.loaded !== 'true') return;
      if (action === 'reset') this.viewer.cameraOrbit = '27deg 73deg 1.02m';
      else {
        const { theta, phi, radius } = this.viewer.getCameraOrbit();
        const distance = clamp(radius * (action === 'in' ? .85 : 1.18), .68, 1.18);
        this.viewer.cameraOrbit = `${theta}rad ${phi}rad ${distance}m`;
      }
    }
    release() {
      this.attempt = (this.attempt || 0) + 1;
      clearTimeout(this.timer); this.pending = false;
      this.viewer?.remove(); this.viewer = null;
      this.stage?.setAttribute('aria-busy', 'false');
      if (this.stage) this.stage.dataset.loaded = 'false';
    }
  }
  if (!customElements.get('dm-scene-viewer')) customElements.define('dm-scene-viewer', DMSceneViewer);
})();
